import { EventBus } from '@hcie/shared';
// We import from the UI package to access rendering functions
import { renderLayers, updateLayerPanel } from '@hcie/canvas-ui';

// Sub-coordinators
import { initDocumentCoordinator, updateZoomUI } from './coordinators/document-coordinator';
import { initToolCoordinator, updateDrawingStatusBar, handleEyeDropper } from './coordinators/tool-coordinator';
import { initSelectionCoordinator } from './coordinators/selection-coordinator';
import { initOverlayCoordinator } from './coordinators/overlay-coordinator';
import { extendHistoryManager } from './coordinators/history-coordinator';
import { bindDrawActionToWindow } from './coordinators/draw-action';
import { VectorToolManager } from './coordinators/vector-tool-manager';
import { initVectorSelectCoordinator } from './coordinators/vector-select-coordinator';
import { historyManager, g, Tool } from '@hcie/core';

// Layer Naming Service (#1325)
import { initSmartLayerNaming } from './services/layer-naming-service';

/**
 * Initializes all UI event listeners and sub-coordinators.
 */
export function initUICoordinator() {
    console.log('[UI-Coordinator] Initializing subscriptions...');

    // PATCH: Register RedEye tool at runtime (as @hcie/core is read-only)
    if (!(Tool as any).RedEye) {
        (Tool as any).RedEye = new Tool('Red Eye Removal', 'btn-red-eye', true);
    }
    
    // Force initial canvas ref lookups
    if ((window as any).initCanvasRefs) (window as any).initCanvasRefs();
    if ((window as any).attachCanvasEvents) (window as any).attachCanvasEvents();

    // Bridge window.settingsManager.settings → g.settings so canvas-ui can read them
    const sm = (window as any).settingsManager;
    if (sm) {
        (g as any).settings = sm.settings;
        // Persisted global toggles mapped to core flat state
        (g as any).shape_fill = !!sm.get?.('vector.fillShape');
        window.addEventListener('hcie-settings-changed', (e: any) => {
            (g as any).settings = e.detail;
            (g as any).shape_fill = !!(e.detail?.vector?.fillShape);
        });
    }

    // Initialize core extensions
    extendHistoryManager(historyManager);
    (window as any).historyManager = historyManager;
    bindDrawActionToWindow();

    // Remove patchWindowHistoryManager — double-patching caused triple HISTORY_CHANGED emits

    // Initialize smart layer naming (#1325)
    initSmartLayerNaming();

    // Initialize sub-coordinators
    initDocumentCoordinator();
    initToolCoordinator();
    initSelectionCoordinator();
    initOverlayCoordinator();
    initVectorSelectCoordinator();

    // Restore vector ghost edit manager (DOM overlay)
    const existingVtm = (window as any).vectorToolManager;
    const needsVtm =
        !existingVtm ||
        typeof existingVtm !== 'object' ||
        typeof existingVtm.commitEdit !== 'function' ||
        typeof existingVtm.cancelEdit !== 'function' ||
        typeof existingVtm.updateOverlay !== 'function' ||
        typeof existingVtm.startNewFromTool !== 'function';
    if (needsVtm) {
        (window as any).vectorToolManager = new VectorToolManager();
    }

    // The Eye Dropper tool initialization has been unified into the ToolCoordinator (#1326)
    // Extra direct listener for mousedown since CANVAS_MOUSE_DOWN is not emitted on EventBus yet
    const canvas = document.getElementById('drawingCanvas') as HTMLCanvasElement;
    if (canvas) {
        canvas.addEventListener('mousedown', (e) => {
            if ((window as any).g?.current_tool?.id === Tool.Eye_Dropper.id) {
                handleEyeDropper(e);
            }
        });
    }

    // ─── Rendering Signals ───────────────────────────────────

    EventBus.on('RENDER_LAYERS', () => {
        console.log('[UI] Signal: RENDER_LAYERS');
        renderLayers();
    });

    EventBus.on('UPDATE_LAYER_PANEL', () => {
        console.log('[UI] Signal: UPDATE_LAYER_PANEL');
        updateLayerPanel();
    });

    // ─── Project IO Signals ──────────────────────────────────

    EventBus.on('PROJECT_LOADED', (data: any) => {
        console.log('[UI] Signal: PROJECT_LOADED', data);
        renderLayers();
        updateLayerPanel();
        
        // Reset history UI if needed
        if ((window as any).historyManager) {
            (window as any).historyManager.clear();
        }
    });

    EventBus.on('PROJECT_RESIZE', (data: { width: number; height: number }) => {
        console.log('[UI] Signal: PROJECT_RESIZE', data);
        if ((window as any).resizeCanvas) {
            (window as any).resizeCanvas(data.width, data.height);
        }
    });

    EventBus.on('PSD_LOADED', (data: any) => {
        console.log('[UI] Signal: PSD_LOADED', data);
        renderLayers();
        updateLayerPanel();
        if ((window as any).resizeCanvas && data.width && data.height) {
            (window as any).resizeCanvas(data.width, data.height);
        }
    });

    // ─── History & Document Signals ──────────────────────────

    EventBus.on('HISTORY_CHANGED', () => {
        // Update history pane if global function exists
        if ((window as any).updateHistoryUI) {
            (window as any).updateHistoryUI();
        }
    });

    EventBus.on('DOCUMENT_SWITCHED', (data: any) => {
        const doc = g.documents[g.activeDocumentIndex];
        if (doc && (window as any).resizeCanvas) {
            (window as any).resizeCanvas(doc.width, doc.height);
        }
        
        // ✨ BUG FIX #41: Synchronize the global 'layers' singleton with the document's layers.
        // Importing modules see the global singleton 'layers', so we MUST update it.
        if (doc && doc.layers && (window as any).layers) {
            const globalLayers = (window as any).layers;
            globalLayers.splice(0, globalLayers.length, ...doc.layers);
            g.activeLayerIndex = doc.activeLayerIndex ?? 0;
            console.log(`[BUG 41] Synchronized global layers for document: ${doc.name}`);
        }

        renderLayers();
        updateLayerPanel();
    });

    EventBus.on('DOCUMENT_TABS_CHANGED', () => {
        if ((window as any).renderImageTabs) {
            (window as any).renderImageTabs();
        }
        
        // BUG FIX #31: Show "New Image" dialog if no documents left
        if (g.documents.length === 0) {
            if ((window as any).openNewImageDialog) {
                (window as any).openNewImageDialog();
            }
        }
    });

    // ─── Tool Signals ────────────────────────────────────────

    EventBus.on('TOOL_CHANGED', (data: { toolId: string }) => {
        console.log('[UI] Signal: TOOL_CHANGED', data.toolId);
        window.dispatchEvent(new CustomEvent('toolChanged', { detail: { tool: data.toolId } }));
    });

    // ─── Startup Logic ───────────────────────────────────────

    // BUG FIX #31: Initial 500px transparent canvas if empty
    setTimeout(() => {
        if (g.documents.length === 0) {
            console.log('[UI-Coordinator] No documents found on startup. Creating default 500x500 transparent document.');
            
            // Set default values in modal first so createNewImage picks them up
            const widthInput = document.getElementById('newWidth') as HTMLInputElement;
            const heightInput = document.getElementById('newHeight') as HTMLInputElement;
            const transparentBg = document.getElementById('transparentBg') as HTMLInputElement;
            
            if (widthInput) widthInput.value = '500';
            if (heightInput) heightInput.value = '500';
            if (transparentBg) transparentBg.checked = true;
            
            if ((window as any).createNewImage) {
                (window as any).createNewImage();
            }
        }
    }, 500); // Small delay to ensure everything is initialized

    console.log('[UI-Coordinator] Subscriptions active.');
}
